const AUTH_ENDPOINT = "/authentication";
const AUTH_ONBOARDING_ENDPOINT = `${AUTH_ENDPOINT}/onboarding`;
const TUTOR_ENDPOINT = "/tutor";
const TUTOR_CALENDAR_ENDPOINT = `${TUTOR_ENDPOINT}/schedule/calendar`;
const TUTOR_TEMPLATES_ENDPOINT = `${TUTOR_ENDPOINT}/schedule/templates`;
const TUTOR_PRICING_ENDPOINT = `${TUTOR_ENDPOINT}/schedule/pricing`;
const TUTOR_SCHEDULE_SETTINGS_ENDPOINT = `${TUTOR_ENDPOINT}/schedule/settings`;
const TUTOR_PROFILE_ENDPOINT = `${TUTOR_ENDPOINT}/profile`;
const TUTOR_UTILS_ENDPOINT = `${TUTOR_ENDPOINT}/utilities`;
const WORKSPACES_ENDPOINT = `/workspaces`;
const WORKSPACES_INVITATIONS_ENDPOINT = `${WORKSPACES_ENDPOINT}/invitations`;
const CONVERSATIONS_ENDPOINT = `/conversations`;
const TUTOR_FINANCES_ENDPOINT = `${TUTOR_ENDPOINT}/finances`;
const STUDENT_ENDPOINT = "/student";

export const API_ENDPOINTS = {
  PUSHER_AUTH: `/broadcasting/auth`,
  AUTH: {
    LOGIN: `${AUTH_ENDPOINT}/login`,
    REGISTER: `${AUTH_ENDPOINT}/register`,
    SSO_VERIFICATION: `${AUTH_ENDPOINT}/verify-sso-token`,
    FINAL_CALLBACK: `${AUTH_ENDPOINT}/callback`,
    ONBOARDING: {
      ACCOUNT_TYPE: `${AUTH_ONBOARDING_ENDPOINT}/select-account-type`,
      PERSONAL_DATA: `${AUTH_ONBOARDING_ENDPOINT}/personal-data`,
      COMMUNICATION_DATA: `${AUTH_ONBOARDING_ENDPOINT}/communication-data`,
      PREFERENCES: `${AUTH_ONBOARDING_ENDPOINT}/preferences`,
      CONFIRMATION: `${AUTH_ONBOARDING_ENDPOINT}/confirmation`,
    },
    USER_DATA: `${AUTH_ENDPOINT}/user`,
  },
  CHAT: {
    CONVERSATION_LIST: CONVERSATIONS_ENDPOINT,
    MANAGE_CONVERSATION: `${CONVERSATIONS_ENDPOINT}/private/management`,
  },
  TUTOR: {
    CALENDAR: {
      GET_DATES: `${TUTOR_CALENDAR_ENDPOINT}/slots`,
      CREATE_SLOT: `${TUTOR_CALENDAR_ENDPOINT}/slots/create`,
      UPDATE_SLOT: `${TUTOR_CALENDAR_ENDPOINT}/slots`,
      DELETE_SLOT: `${TUTOR_CALENDAR_ENDPOINT}/slots`,
    },
    TEMPLATES: {
      LIST: TUTOR_TEMPLATES_ENDPOINT,
    },
    SCHEDULE: {
      SETTINGS: TUTOR_SCHEDULE_SETTINGS_ENDPOINT,
      BOOKING_CONSTRAINTS: `${TUTOR_SCHEDULE_SETTINGS_ENDPOINT}/booking-constraints`,
      DEFAULT_TEMPLATE: `${TUTOR_SCHEDULE_SETTINGS_ENDPOINT}/default-template`,
      PRICING: `${TUTOR_PRICING_ENDPOINT}`,
    },
    FINANCES: {
      CREDITS_OVERVIEW: `${TUTOR_FINANCES_ENDPOINT}/credits/overview`,
      CREDITS_ONBOARDING: `${TUTOR_FINANCES_ENDPOINT}/credits/onboarding`,
      CREDITS_PURCHASE: `${TUTOR_FINANCES_ENDPOINT}/credits/management/purchase`,
      CREDITS_PRICING: `${TUTOR_FINANCES_ENDPOINT}/credits/management/pricing`,
      CHECKOUT_STATUS: `${TUTOR_FINANCES_ENDPOINT}/credits/management/checkout-status`,
      PAYMENTS_LISTING: `${TUTOR_FINANCES_ENDPOINT}/credits/payments`,
      PAYMENTS_STUDENTS: `${TUTOR_FINANCES_ENDPOINT}/credits/payments/students`,
      PAYMENTS_LESSONS: `${TUTOR_FINANCES_ENDPOINT}/credits/payments/lessons`,
      PAYMENTS_CREATE: `${TUTOR_FINANCES_ENDPOINT}/credits/payments/create`,
      PAYMENT_DETAILS: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}`,
      PAYMENT_SEND_REMINDER: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/notify`,
      PAYMENT_UPDATE: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/update`,
      PAYMENT_CONFIRM: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/confirm`,
      PAYMENT_REJECT: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/reject`,
      PAYMENT_DELETE: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/delete`,
      PAYMENT_RESTORE: (paymentId: string) =>
        `${TUTOR_FINANCES_ENDPOINT}/credits/payments/${paymentId}/restore`,
    },
    PROFILE: {
      GET_PROFILE_DATA: TUTOR_PROFILE_ENDPOINT,
      CREATE_PROFILE: `${TUTOR_PROFILE_ENDPOINT}/create`,
      CREATE_PROFILE_NL: `${TUTOR_PROFILE_ENDPOINT}/create/new-locale`,
      EDIT_PROFILE: `${TUTOR_PROFILE_ENDPOINT}/update`,
      GET_SETTINGS: `${TUTOR_PROFILE_ENDPOINT}/settings`,
      EDIT_SETTINGS: `${TUTOR_PROFILE_ENDPOINT}/settings`,
    },
    UTILS: {
      LANGUAGES: `${TUTOR_UTILS_ENDPOINT}/languages`,
      SUBJECTS: `${TUTOR_UTILS_ENDPOINT}/subjects`,
      CURRICULUM_LEVELS: `${TUTOR_UTILS_ENDPOINT}/curriculum-levels`,
      LOCATION: `${TUTOR_UTILS_ENDPOINT}/localization`,
    },
  },
  STUDENT: {
    PAYMENTS: {
      LISTING: `${STUDENT_ENDPOINT}/payments`,
      DETAILS: (paymentId: string) => `${STUDENT_ENDPOINT}/payments/${paymentId}`,
      SEND_CONFIRMATION: (paymentId: string) =>
        `${STUDENT_ENDPOINT}/payments/${paymentId}/confirmations`,
    },
  },
  WORKSPACES: {
    WORKSPACES: `${WORKSPACES_ENDPOINT}`,
    WIDGET: `${WORKSPACES_ENDPOINT}/widget`,
    TUTOR_LIST: `${WORKSPACES_ENDPOINT}/listing/tutor`,
    STUDENT_LIST: `${WORKSPACES_ENDPOINT}/listing/student`,
    INVITATIONS: {
      INVITATIONS: `${WORKSPACES_INVITATIONS_ENDPOINT}`,
      CREATE: `${WORKSPACES_INVITATIONS_ENDPOINT}/create`,
      SEARCH_STUDENTS: `${WORKSPACES_INVITATIONS_ENDPOINT}/search-students`,
    },
    SETTINGS: {
      TUTOR: (workspaceId: string) => `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor`,
      STUDENT: (workspaceId: string) => `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/student`,
      UPDATE_NAME: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/name`,
      UPDATE_LOGO: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/logo`,
      DELETE_LOGO: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/logo`,
      DISABLE_RESERVATIONS: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/disable-reservations`,
      ENABLE_RESERVATIONS: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/enable-reservations`,
      BLOCK_STUDENT: (workspaceId: string, workspaceUserId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/student/${workspaceUserId}/block`,
      UNBLOCK_STUDENT: (workspaceId: string, workspaceUserId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/student/${workspaceUserId}/unblock`,
      BLOCK_WORKSPACE: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/student/block`,
      UNBLOCK_WORKSPACE: (workspaceId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/student/unblock`,
    },
  },
} as const;
export const PUBLIC_PROFILE_ENDPOINTS = (profileId: string) => {
  return {
    TUTOR: {
      GET_PROFILE_DATA: `marketplace/${profileId}/profile`,
      GET_REVIEWS: `marketplace/${profileId}/profile/reviews`,
    },
  };
};

export const WORKSPACES_API_ENDPOINTS = (workspaceId: string) => {
  return {
    STUDENT: {
      RESERVE_SLOT: `${WORKSPACES_ENDPOINT}/${workspaceId}/lessons/student/reservations`,
      SCHEDULE: `${WORKSPACES_ENDPOINT}/${workspaceId}/schedule/students/calendar/slots`,
      LEAVE: `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/student/leave`,
      REJOIN: `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/student/rejoin`,
      PREVIEW: `${WORKSPACES_ENDPOINT}/invitations/${workspaceId}/preview`,
    },
    TUTOR: {
      BLOCK_STUDENT: `${WORKSPACES_ENDPOINT}/${workspaceId}/management/tutor/block-student`,
      LEAVE: `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/leave`,
      REJOIN: `${WORKSPACES_ENDPOINT}/${workspaceId}/settings/tutor/rejoin`,
      GET_LISTINGS: `${WORKSPACES_ENDPOINT}/${workspaceId}/lessons/tutor/listing`,
      EXTEND_LESSON: (lessonId: string) =>
        `${WORKSPACES_ENDPOINT}/${workspaceId}/lessons/tutor/${lessonId}/extend`,
    },
    LESSON_CONVERSATION: `${WORKSPACES_ENDPOINT}/${workspaceId}/lessons/conversation`,
  };
};
